<template>
  <div>
    <div class="is-flex is-justify-content-space-between is-align-items-center">
      <b-breadcrumb size="is-large" separator="has-succeeds-separator" aria-label="breadcrumbs">
        <b-breadcrumb-item active>
          {{ $t('integrations.title') }}
        </b-breadcrumb-item>
      </b-breadcrumb>
      <b-button type="is-primary" @click="$router.push({ name: 'integration-gallery' })">{{ $t('integrations.addNew') }}</b-button>
    </div>
    <div class="list-table-container">
      <b-table :data="rows" striped :loading="loading" @click="handleRowClick"
        sort-icon="menu-up" default-sort="name" default-sort-direction="asc">
        <b-table-column field="name" sortable :label="$t('integrations.name')" v-slot="props">
          {{ props.row.name }}
        </b-table-column>
        <b-table-column field="type" sortable :label="$t('integrations.type')" v-slot="props">
          <span class="is-flex is-align-items-center">
            <b-tooltip :label="getTemplateLabel(props.row.type)" position="is-right">
              <b-icon :icon="getTemplateIcon(props.row.type)" class="mr-2" />
            </b-tooltip>
          </span>
        </b-table-column>
        <b-table-column field="url" sortable :label="$t('integrations.urlOrLanguage')" v-slot="props">
          <template v-if="props.row.url">
            {{ props.row.url }}
          </template>
          <template v-else-if="props.row.language">
            <span class="is-flex is-align-items-center">
              <b-icon :icon="getLanguageIcon(props.row.language)" class="mr-2" />
              {{ getLanguageLabel(props.row.language) }}
            </span>
          </template>
          <template v-else>
            —
          </template>
        </b-table-column>
        <b-table-column field="disabled" sortable :label="$t('integrations.status')" v-slot="props">
          {{ !props.row.disabled ? $t('integrations.enabled') : $t('integrations.disabled') }}
        </b-table-column>
        <b-table-column :label="$t('global.action')" v-slot="props">
          <div class="control is-flex is-align-items-center">
            <b-button type="is-info" size="is-small" icon-left="play" :title="$t('integrations.test')" aria-label="test"
            class="mr-2" @click.stop="openTestModal(props.row)" />
            <b-button type="is-danger" size="is-small" icon-left="delete" :title="$t('global.delete')" aria-label="delete" @click.stop="confirmRemove(props.row)" />
          </div>
        </b-table-column>
        <template #empty>
          <div class="has-text-centered mt-4 mb-4">{{ $t('integrations.noneYet') }}</div>
        </template>
      </b-table>
    </div>
    <b-modal v-model="testModalOpen" has-modal-card trap-focus :destroy-on-hide="false" aria-role="dialog" :can-cancel="['escape','x','outside']" :aria-label="$t('integrations.testWebhook')" close-button-aria-label="Close" aria-modal>
      <template #default="props">
        <div class="modal-card" style="width: 640px; max-width: 95vw;">
          <header class="modal-card-head">
            <p class="modal-card-title">{{ $t('integrations.testWebhook') }}</p>
            <button class="delete" aria-label="close" @click="props.close"></button>
          </header>
          <section class="modal-card-body">
            <b-field :label="$t('integrations.device')">
              <b-select v-model="testDeviceId">
                <option v-for="d in deviceList" :key="d.id" :value="d.id">{{ d.name || d.id }}</option>
              </b-select>
            </b-field>
            <b-field :label="$t('integrations.eventData')">
              <b-input type="textarea" :rows="6" v-model="testEventData" :placeholder="$t('integrations.eventDataPlaceholder')" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <div class="buttons">
              <b-button type="is-primary" :disabled="!testDeviceId" @click="publishTestEvent">{{ $t('integrations.test') }}</b-button>
              <b-button @click="props.close">{{ $t('global.close') }}</b-button>
            </div>
          </footer>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, getCurrentInstance, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import KeyCloakService from '@/security/KeycloakService'
import { createMqttClient } from '@/services/mqtt'
import { useIntegrationTemplates } from '@/utils/integrationTemplates'

type IntegrationRow = { id: string; name: string; type: string; content?: Record<string, unknown>; url?: string; disabled?: boolean }

export default defineComponent({
  name: 'IntegrationList',
  setup() {
    const router = useRouter()
    const store = useStore()
    const { getTemplateLabel, getTemplateIcon, getLanguageIcon, getLanguageLabel } = useIntegrationTemplates()
    const rows = computed<IntegrationRow[]>(() => {
      const list = Object.values(store.state.integrations) as IntegrationRow[]
      return list.map(r => ({
        ...r,
        url: (r as any).content && (r as any).content.url ? String((r as any).content.url) : '',
        language: (r as any).content && (r as any).content.lambda ? String((r as any).content.lambda.language) : '',
        disabled: ((r as any).content && (r as any).content.disabled === true)
      }))
    })
    const loading = computed<boolean>(() => store.state.loading.integrations)
    const vm = getCurrentInstance()

    const load = async (force = false) => {
      await store.dispatch('loadModel', { model: 'integrations', force })
      await store.dispatch('loadModel', { model: 'devices', force })
    }

    const remove = async (row: IntegrationRow) => {
      try {
        await store.dispatch('deleteIntegration', { id: row.id })
      } catch (e) {
        // ignore
      }
    }

    const confirmRemove = (row: IntegrationRow) => {
      type DialogProxy = { $buefy?: { dialog?: { confirm?: (opts: { message: string; type?: string; confirmText?: string; cancelText?: string; hasIcon?: boolean; onConfirm?: () => void }) => void } } }
      type I18nProxy = { $t?: (key: string, vars?: Record<string, string>) => string }
      const proxy = vm?.proxy as unknown as DialogProxy & I18nProxy
      const msg = proxy?.$t ? proxy.$t('integrations.confirmDelete', { name: row.name }) : `Delete integration ${row.name}?`
      const confirmText = proxy?.$t ? proxy.$t('global.delete') : 'Delete'
      const cancelText = proxy?.$t ? proxy.$t('global.cancel') : 'Cancel'
      proxy?.$buefy?.dialog?.confirm?.({
        message: msg,
        type: 'is-danger',
        confirmText,
        cancelText,
        hasIcon: true,
        onConfirm: () => { remove(row) }
      })
    }

    const handleRowClick = (row: IntegrationRow) => {
      router.push({ name: 'integration-edit', params: { id: row.id } })
    }

    const edit = (row: IntegrationRow) => {
      router.push({ name: 'integration-edit', params: { id: row.id } })
    }

    // Test webhook modal state
    const testModalOpen = ref(false)
    const testEventData = ref('')
    const testDeviceId = ref('')
    const currentIntegration = ref<IntegrationRow | null>(null)
    type DeviceSummary = { id: string; name?: string }
    const deviceList = computed<DeviceSummary[]>(() => Object.values(store.state.devices) as DeviceSummary[])

    // MQTT client for publishing test events
    const mqttClient = ref<any>(null)
    const ensureMqttConnected = async (): Promise<void> => {
      if (mqttClient.value && mqttClient.value.connected) return
      mqttClient.value = await createMqttClient({ clientIdPrefix: 'console-app', reconnectPeriodMs: 1000 })
      await new Promise<void>((resolve, reject) => {
        mqttClient.value.on('connect', () => resolve())
        mqttClient.value.on('error', (err: unknown) => reject(err))
      })
    }

    type ToastProxy = { $buefy?: { toast?: { open?: (opts: { message: string; type: string }) => void } } }
    const openToast = (message: string, type: string) => {
      const proxy = vm?.proxy as unknown as ToastProxy
      proxy?.$buefy?.toast?.open?.({ message, type })
    }

    const openTestModal = (row: IntegrationRow) => {
      currentIntegration.value = row
      testEventData.value = ''
      const first = deviceList.value[0]
      testDeviceId.value = first ? first.id : ''
      testModalOpen.value = true
    }

    const publishTestEvent = async () => {
      if (!currentIntegration.value || !testDeviceId.value) return
      try {
        await ensureMqttConnected()
        const topic = `devices/${testDeviceId.value}/webhooks/${currentIntegration.value.name}`
        const payload = testEventData.value || ''
        mqttClient.value.publish(topic, payload, { qos: 0 }, (err: unknown) => {
          if (err) {
            openToast(((vm?.proxy as any)?.$t?.('integrations.toastPublishFailed')), 'is-danger')
          } else {
            openToast(((vm?.proxy as any)?.$t?.('integrations.toastPublished')), 'is-success')
            testModalOpen.value = false
          }
        })
      } catch (e) {
        openToast(((vm?.proxy as any)?.$t?.('integrations.toastPublishFailed')), 'is-danger')
      }
    }

    onMounted(load)
    return { rows, loading, remove, confirmRemove, handleRowClick, edit, testModalOpen, testEventData, testDeviceId, deviceList, openTestModal, publishTestEvent, getTemplateLabel, getTemplateIcon, getLanguageIcon, getLanguageLabel }
  }
})
</script>


 
